/**
 * PDP third-party accessibility remediations (theme-owned mounts).
 *
 * - Stylitics: ensure region is labelled by #widget-title
 * - Spangle: host section already has aria-label; keep Discover heading if missing
 * - Rebuy: name focusable product grid; take focusables inside aria-hidden out of tab order
 *
 * Guarded so the script is safe if included more than once.
 */
(() => {
  if (window.__akPdpThirdpartyA11y) return;
  window.__akPdpThirdpartyA11y = true;

  const labelStylitics = () => {
    const title = document.getElementById('widget-title');
    const region = document.getElementById('stylitics-widget-container');
    if (!region) return;

    if (title?.id) {
      region.setAttribute('aria-labelledby', title.id);
      region.removeAttribute('aria-label');
    } else if (!region.getAttribute('aria-label')) {
      region.setAttribute('aria-label', 'How to Style It');
    }

    if (!region.getAttribute('role')) {
      region.setAttribute('role', 'region');
    }
  };

  const labelSpangleHost = () => {
    const host = document.querySelector(
      '.ak-product-extras section.section--has-sidebar-option, section.section--has-sidebar-option'
    );
    if (!host) return;

    if (!host.getAttribute('aria-label')) {
      host.setAttribute('aria-label', 'Product recommendations');
    }

    if (!host.querySelector(':scope > .ak-spangle-region-title')) {
      const heading = document.createElement('h2');
      heading.className = 'ak-spangle-region-title visually-hidden';
      heading.textContent = 'Discover More';
      host.prepend(heading);
    }
  };

  /**
   * @param {ParentNode} root
   */
  const syncRebuyHiddenFocusables = (root) => {
    const candidates = root.querySelectorAll(
      'a[href], button, input, select, textarea, summary, [tabindex]'
    );

    for (const el of candidates) {
      if (!(el instanceof HTMLElement)) continue;

      const hiddenAncestor = el.closest('[aria-hidden="true"]');
      const isHidden = !!(hiddenAncestor && root.contains(hiddenAncestor));

      if (isHidden) {
        if (!el.hasAttribute('data-ak-rebuy-tabindex')) {
          el.setAttribute('data-ak-rebuy-tabindex', el.getAttribute('tabindex') ?? '');
        }
        el.setAttribute('tabindex', '-1');
        continue;
      }

      if (!el.hasAttribute('data-ak-rebuy-tabindex')) continue;

      const prev = el.getAttribute('data-ak-rebuy-tabindex');
      el.removeAttribute('data-ak-rebuy-tabindex');

      if (prev === '' || prev == null) {
        el.removeAttribute('tabindex');
      } else {
        el.setAttribute('tabindex', prev);
      }
    }
  };

  /**
   * @param {Element} widget
   */
  const labelRebuyGrid = (widget) => {
    const grid = widget.querySelector('.rebuy-product-grid');
    if (!grid) return;

    const title =
      widget.querySelector('.rebuy-title, .rebuy-widget-title, h2, h3')?.textContent?.trim() ||
      'You May Also Like';

    if (!grid.getAttribute('aria-label') && !grid.getAttribute('aria-labelledby')) {
      grid.setAttribute('aria-label', title);
    }

    if (grid.getAttribute('tabindex') === '0' && !grid.getAttribute('role')) {
      grid.setAttribute('role', 'region');
    }
  };

  /**
   * @param {ParentNode} [scope]
   */
  const remediateRebuy = (scope = document) => {
    const widgets = scope.querySelectorAll(
      '.rebuy-widget, .ak-rebuy-widget .rebuy-widget, .ak-rebuy-widget, .ak-rebuy-mount .rebuy-widget, .ak-rebuy-mount, [data-rebuy-id] .rebuy-widget, [data-rebuy-id]'
    );

    for (const widget of widgets) {
      if (!(widget instanceof Element)) continue;
      // Prefer hydrated widget node when present
      const root =
        widget.classList?.contains('rebuy-widget')
          ? widget
          : widget.querySelector('.rebuy-widget') || widget;

      if (!root.querySelector('.rebuy-product-grid, .rebuy-product-block, a, button, select')) {
        continue;
      }

      labelRebuyGrid(root);
      syncRebuyHiddenFocusables(root);
    }
  };

  const run = () => {
    labelStylitics();
    labelSpangleHost();
    remediateRebuy();
  };

  const schedule = () => {
    run();
    requestAnimationFrame(run);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', schedule, { once: true });
  } else {
    schedule();
  }

  // Rebuy / Stylitics hydrate asynchronously and remorph on carousel changes.
  const observer = new MutationObserver(() => {
    window.clearTimeout(observer._akTimer);
    observer._akTimer = window.setTimeout(run, 80);
  });

  observer.observe(document.documentElement, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ['aria-hidden', 'class', 'tabindex'],
  });

  // Late app boots
  window.setTimeout(run, 2000);
  window.setTimeout(run, 5000);
  window.setTimeout(run, 10000);
})();
